import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Box, Typography } from '@mui/material';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ActionLink, ListCollapsibleRow, ListRow, ListRowNoData, ListRowSkeleton } from 'app/components';
import { MuiCheckboxStyled } from 'app/components/Checkbox/Checkbox.styles';
import { EditableFieldFactory } from 'app/components/FormikField/Factory/EditableFieldFactory';
import { TranslationKeys } from 'app/translations';
import type { EmployerDataFilter, SubOwnerDataFilter } from 'app/types';

import type { UserAccessesListOnChangeSelected, UserAccessesListSelectedData } from '../../UserAccessesList.types';
import {
  areAnyEmployerIdsSelected,
  areEmployerIdsSelected,
  getEmployerIds,
  isEmployerSelected,
  toggleAllEmployers,
  toggleEmployerIds,
  toggleEmployerSelection,
  toggleParentEmployerIds,
  toggleSubEmployerSelection,
} from '../../UserAccessesList.utils';
import { UserAccessesListContainer } from '../UserAccessesListContainer';
import { UserAccessesListHeader } from '../UserAccessesListHeader';

type UserAccessesListDataFilterProps = {
  isLoading?: boolean;
  isLoggedInUser?: boolean;
  onChangeSelected: UserAccessesListOnChangeSelected;
  selectedData: UserAccessesListSelectedData | undefined;
  results: Array<SubOwnerDataFilter | EmployerDataFilter> | undefined;
  selectedEventFlows: string[];
  openConfirmationDialog: (value: boolean) => void;
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
};

type EmployerHeaderProps = {
  employer: EmployerDataFilter;
  selected: boolean;
  selectedChildCount?: number;
  eventFlowDisabled: boolean;
  isEventFlowSelected: boolean;
  onToggleEventFlow: () => void;
};

const EmployerHeader = ({
  employer,
  selected,
  selectedChildCount,
  eventFlowDisabled,
  isEventFlowSelected,
  onToggleEventFlow,
}: EmployerHeaderProps) => (
  <>
    <Typography>
      <ActionLink to={`/employers/${employer.employerId}`}>
        {employer.name}
        {selectedChildCount ? ` (${selectedChildCount})` : ''}
      </ActionLink>
    </Typography>
    <Typography sx={{ marginLeft: 'auto', padding: '0 10px 0 0' }}>
      <MuiCheckboxStyled
        color={'secondary'}
        onClick={onToggleEventFlow}
        checked={selected && isEventFlowSelected}
        disabled={!selected || eventFlowDisabled}
      />
      <Typography variant={'caption'} textTransform={'initial'}>
        <FormattedMessage id={TranslationKeys.users_accessesList_defaultEventFlowUser} />
      </Typography>
    </Typography>
  </>
);

const isSubOwnerResult = (result: SubOwnerDataFilter | EmployerDataFilter): result is SubOwnerDataFilter =>
  'subOwnerId' in result;

export const UserAccessesListDataFilter = ({
  results,
  selectedData,
  onChangeSelected,
  selectedEventFlows,
  openConfirmationDialog,
  isLoggedInUser = false,
  isLoading = false,
  searchQuery = '',
  onSearchQueryChange,
}: UserAccessesListDataFilterProps) => {
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const disabled = isLoggedInUser || isLoading || !!selectedData?.allEmployers;
  const eventFlowDisabled = isLoggedInUser || isLoading;
  const pageEmployerIds = getEmployerIds(
    results?.flatMap(result => (isSubOwnerResult(result) ? result.employers : [result])) || [],
  );
  const onSelectAll = () => selectedData && onChangeSelected(toggleAllEmployers(selectedData));
  const onSelectAllOnPage = () => selectedData && onChangeSelected(toggleEmployerIds(selectedData, pageEmployerIds));
  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onSearchQueryChange?.(event.target.value), 300);
  };
  const toggleEventFlow = (employerId: string, eventFlowDefaultUser?: string | null) => () => {
    if (!selectedData) return;
    const eventFlows = selectedData.eventFlows || [];
    openConfirmationDialog(eventFlowDefaultUser != null);
    onChangeSelected({
      ...selectedData,
      eventFlows: eventFlows.includes(employerId)
        ? eventFlows.filter(id => id !== employerId)
        : [...eventFlows, employerId],
    });
  };
  const renderEmployer = (employer: EmployerDataFilter, prefix = '') => {
    const childEmployerIds = getEmployerIds(employer.subEmployers);
    const selectedChildCount = childEmployerIds.filter(employerId =>
      isEmployerSelected(selectedData, employerId),
    ).length;
    const selected =
      isEmployerSelected(selectedData, employer.employerId) ||
      areAnyEmployerIdsSelected(selectedData, childEmployerIds);
    const header = (
      <EmployerHeader
        employer={employer}
        selected={selected}
        selectedChildCount={selectedChildCount}
        eventFlowDisabled={eventFlowDisabled}
        isEventFlowSelected={selectedEventFlows.includes(employer.employerId)}
        onToggleEventFlow={toggleEventFlow(employer.employerId, employer.eventFlowDefaultUser)}
      />
    );
    const select = () =>
      !selectedData?.allEmployers && selectedData && onChangeSelected(toggleEmployerSelection(selectedData, employer));
    if (!employer.subEmployers?.length)
      return (
        <ListRow key={`${prefix}${employer.employerId}`} selected={selected} onSelect={select} disabled={disabled}>
          {header}
        </ListRow>
      );
    return (
      <ListCollapsibleRow
        key={`${prefix}${employer.employerId}`}
        selected={selected}
        onSelect={select}
        disabled={disabled}
        rowHeader={header}
      >
        {employer.subEmployers.map(child => {
          const isChildSelected = isEmployerSelected(selectedData, child.employerId);
          return (
            <ListRow
              key={`${prefix}${employer.employerId}_${child.employerId}`}
              selected={isChildSelected}
              onSelect={() =>
                !selectedData?.allEmployers &&
                selectedData &&
                onChangeSelected(toggleSubEmployerSelection(selectedData, employer.employerId, child.employerId))
              }
              disabled={disabled}
              innerRow
            >
              <EmployerHeader
                employer={child}
                selected={isChildSelected}
                eventFlowDisabled={eventFlowDisabled}
                isEventFlowSelected={selectedEventFlows.includes(child.employerId)}
                onToggleEventFlow={toggleEventFlow(child.employerId, child.eventFlowDefaultUser)}
              />
            </ListRow>
          );
        })}
      </ListCollapsibleRow>
    );
  };

  return (
    <Box sx={theme => ({ marginTop: theme.spacing(2) })}>
      <UserAccessesListHeader
        headerTextId={TranslationKeys.users_accessesList_employers}
        onChange={onSearch}
        isLoading={isLoading}
        disabled={isLoggedInUser}
        onSelectAll={onSelectAll}
        onSelectAllOnPage={onSelectAllOnPage}
        areAllSelected={!!selectedData?.allEmployers}
        areAllOnPageSelected={areEmployerIdsSelected(selectedData, pageEmployerIds)}
        noData={!results?.length}
        totalCount={0}
        hasSearchQuery={!!searchQuery}
      />
      <EditableFieldFactory
        editing={true}
        field={{
          type: 'checkbox',
          name: 'autoAssignEmployers',
          label: useIntl().formatMessage({ id: TranslationKeys.users_accessesList_autoAssignEmployer }),
        }}
        value={selectedData?.autoAssignEmployers}
        onChange={(event: any) =>
          selectedData && onChangeSelected({ ...selectedData, autoAssignEmployers: event.value })
        }
      />
      <UserAccessesListContainer>
        {isLoading && <ListRowSkeleton />}
        {!isLoading &&
          results?.map(result =>
            isSubOwnerResult(result)
              ? (() => {
                  const subOwnerEmployerIds = getEmployerIds(result.employers);
                  const selectedEmployerCount = subOwnerEmployerIds.filter(employerId =>
                    isEmployerSelected(selectedData, employerId),
                  ).length;

                  return (
                    <ListCollapsibleRow
                      key={result.subOwnerId}
                      selected={areAnyEmployerIdsSelected(selectedData, subOwnerEmployerIds)}
                      onSelect={
                        result.employers.length
                          ? () =>
                              selectedData &&
                              onChangeSelected(toggleParentEmployerIds(selectedData, subOwnerEmployerIds))
                          : () => undefined
                      }
                      disabled={disabled || !result.employers.length}
                      rowHeader={
                        <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <FiberManualRecordIcon sx={{ fontSize: 7 }} />
                          <ActionLink to={`/sub-owners/${result.subOwnerId}`}>
                            {result.name}
                            {selectedEmployerCount ? ` (${selectedEmployerCount})` : ''}
                          </ActionLink>
                        </Typography>
                      }
                    >
                      {result.employers.length ? (
                        result.employers.map(employer => renderEmployer(employer, `${result.subOwnerId}_`))
                      ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '8px 0 !important' }}>
                          <Typography variant={'body2'}>
                            <FormattedMessage id={TranslationKeys.employers_noEmployers} />
                          </Typography>
                        </Box>
                      )}
                    </ListCollapsibleRow>
                  );
                })()
              : renderEmployer(result),
          )}
        {!isLoading && !results?.length && <ListRowNoData searchQuery={searchQuery} />}
      </UserAccessesListContainer>
    </Box>
  );
};
